import React, { useState, useEffect } from "react";
import Modal from "../../components/Modal";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import Swal from "sweetalert2";
import { Plus, Trash2, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { useGlobalContext } from "../../context/stateContext";
import { showApiErrors } from "../../utils/helpers";

const SupplyItem = ({ modalOpen, setModalOpen, item }) => {
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const [submittingForm, setSubmittingForm] = useState(false);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [selectedBatches, setSelectedBatches] = useState([
    { batch_id: "", quantity: "", batch_data: null },
  ]);

  useEffect(() => {
    if (modalOpen && item?.drug_id) {
      fetchBatches();
    }
  }, [modalOpen, item]);

  const fetchBatches = async () => {
    try {
      setLoadingBatches(true);
      const data = await requestWithAuth(
        `/inventory/branch/batches/list?drug=${item?.drug_id}&branch=${activeSession?.branch?.id}&paginated=false`,
        "GET"
      );
      setBatches(data || []);
    } catch (error) {
      toast.error("Failed to load batches");
    } finally {
      setLoadingBatches(false);
    }
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    return expiry < today;
  };

  const addBatchRow = () => {
    setSelectedBatches([
      ...selectedBatches,
      { batch_id: "", quantity: "", batch_data: null },
    ]);
  };

  const removeBatchRow = (index) => {
    const newBatches = selectedBatches.filter((_, i) => i !== index);
    setSelectedBatches(newBatches);
  };

  const handleBatchSelect = (index, batchId) => {
    const selectedBatch = batches.find((b) => b.id === parseInt(batchId));

    if (selectedBatch && isExpired(selectedBatch.expiry_date)) {
      Swal.fire({
        icon: "error",
        title: "Expired Batch!",
        text: `This batch expired on ${new Date(
          selectedBatch.expiry_date
        ).toLocaleDateString()}. You cannot supply expired items.`,
      });
      return;
    }

    // Check if this batch is already selected in another row
    const isDuplicate = selectedBatches.some(
      (batch, i) => i !== index && batch.batch_id === batchId && batchId !== ""
    );

    if (isDuplicate) {
      Swal.fire({
        icon: "error",
        title: "Duplicate Batch!",
        text: "This batch has already been selected. Please choose a different batch.",
      });
      return;
    }

    const newBatches = [...selectedBatches];
    newBatches[index] = {
      ...newBatches[index],
      batch_id: batchId,
      batch_data: selectedBatch,
    };
    setSelectedBatches(newBatches);
  };

  const handleQuantityChange = (index, quantity) => {
    const newBatches = [...selectedBatches];
    const batch = newBatches[index].batch_data;

    if (batch && parseInt(quantity) > parseInt(batch.quantity)) {
      toast.warning(
        `Quantity cannot exceed available stock (${batch.quantity})`
      );
      return;
    }

    newBatches[index] = {
      ...newBatches[index],
      quantity: quantity,
    };
    setSelectedBatches(newBatches);
  };

  const getTotalSupplied = () => {
    return selectedBatches.reduce((sum, batch) => {
      return sum + (parseInt(batch.quantity) || 0);
    }, 0);
  };

  const validateForm = () => {
    // Check if at least one batch is selected
    if (selectedBatches.every((b) => !b.batch_id)) {
      toast.error("Please select at least one batch");
      return false;
    }

    // Check if all selected batches have quantities
    for (let i = 0; i < selectedBatches.length; i++) {
      const batch = selectedBatches[i];
      if (batch.batch_id && !batch.quantity) {
        toast.error(`Please enter quantity for batch ${i + 1}`);
        return false;
      }
      if (!batch.batch_id && batch.quantity) {
        toast.error(`Please select a batch for row ${i + 1}`);
        return false;
      }
    }

    // Check if total supplied exceeds requested
    const totalSupplied = getTotalSupplied();

    if (totalSupplied === 0) {
      toast.error("Total supplied quantity must be greater than 0");
      return false;
    }

    return true;
  };

  const handleSupply = async () => {
    if (!validateForm()) {
      return;
    }

    const totalSupplied = getTotalSupplied();
    const batchDetails = selectedBatches
      .filter((b) => b.batch_id && b.quantity)
      .map((b) => ({
        batch_id: b.batch_id,
        quantity: parseInt(b.quantity),
        batch_number: b.batch_data?.batch_number,
        expiry_date: b.batch_data?.expiry_date,
      }));

    Swal.fire({
      icon: "question",
      title: "Confirm Supply",
      html: `
        <div style="text-align: left;">
          <p><strong>Total Quantity:</strong> ${totalSupplied}</p>
          <p><strong>Batches:</strong> ${batchDetails.length}</p>
          <hr style="margin: 10px 0;">
          ${batchDetails
            .map(
              (b, i) => `
            <p style="margin: 5px 0;">
              Batch ${i + 1}: ${b.batch_number} (Qty: ${b.quantity})
            </p>
          `
            )
            .join("")}
        </div>
      `,
      showDenyButton: true,
      confirmButtonText: "Yes, Supply!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setSubmittingForm(true);
          const body = JSON.stringify({
            quantity_supplied: totalSupplied,
            batches: batchDetails,
          });

          await requestWithAuth(
            `/inventory/requisition-item/${item?.id}/supply/`,
            "PUT",
            body,
            {
              "Content-Type": "application/json",
            }
          );

          Swal.fire({
            icon: "success",
            title: "Supplied!",
            text: "Items Supplied Successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          showApiErrors(error);
          console.error(error);
        } finally {
          setSubmittingForm(false);
        }
      }
    });
  };

  const getExpiryStatusBadge = (expiryDate) => {
    if (!expiryDate) return null;

    const expired = isExpired(expiryDate);
    const daysUntilExpiry = Math.ceil(
      (new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24)
    );

    if (expired) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
          <XCircle size={12} />
          Expired
        </span>
      );
    } else if (daysUntilExpiry <= 30) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
          <AlertCircle size={12} />
          Expires Soon
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
          <CheckCircle size={12} />
          Valid
        </span>
      );
    }
  };

  return (
    <div>
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Supply Details - Batch Selection"
      >
        <div>
          {/* Item Information */}
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">
                  Drug Name
                </label>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {item?.drug}
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">
                  Requested Quantity
                </label>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {item?.quantity}
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">
                  Available Stock
                </label>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {item?.total_remaining}
                </p>
              </div>
            </div>
          </div>

          {/* Batch Selection Section */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Select Batches to Supply
              </h3>
              <button
                type="button"
                onClick={addBatchRow}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Plus size={14} />
                Add Batch
              </button>
            </div>

            {loadingBatches ? (
              <div className="flex items-center justify-center py-8">
                <RotatingLines strokeColor="blue" height="30" width="30" />
              </div>
            ) : (
              <div className="space-y-3">
                {selectedBatches.map((selectedBatch, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 bg-white"
                  >
                    <div className="grid grid-cols-12 gap-3 items-start">
                      {/* Batch Selection */}
                      <div className="col-span-5">
                        <label className="text-xs font-medium text-gray-700 mb-1 block">
                          Batch Number
                        </label>
                        <select
                          value={selectedBatch.batch_id}
                          onChange={(e) =>
                            handleBatchSelect(index, e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select batch...</option>
                          {batches.map((batch) => (
                            <option
                              key={batch.id}
                              value={batch.id}
                              disabled={isExpired(batch.expiry_date)}
                              title={batch.expiry_date}
                            >
                              {batch.batch_number} - Stock: {batch.quantity}
                              {isExpired(batch.expiry_date) ? " (EXPIRED)" : ""}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Quantity Input */}
                      <div className="col-span-3">
                        <label className="text-xs font-medium text-gray-700 mb-1 block">
                          Quantity
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={selectedBatch.quantity}
                          onChange={(e) =>
                            handleQuantityChange(index, e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={!selectedBatch.batch_id}
                        />
                      </div>

                      {/* Batch Info */}
                      <div className="col-span-3">
                        {selectedBatch.batch_data ? (
                          <div className="text-xs space-y-1">
                            <p className="text-gray-600">
                              Expires:{" "}
                              <span className="font-medium text-gray-900">
                                {new Date(
                                  selectedBatch.batch_data.expiry_date
                                ).toLocaleDateString()}
                              </span>
                            </p>
                            <div>
                              {getExpiryStatusBadge(
                                selectedBatch.batch_data.expiry_date
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">
                            Select a batch
                          </p>
                        )}
                      </div>

                      {/* Remove Button */}
                      <div className="col-span-1 flex items-end h-full">
                        {selectedBatches.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeBatchRow(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Total Quantity to Supply:
              </span>
              <span className="text-2xl font-bold text-blue-600">
                {getTotalSupplied()}
              </span>
            </div>
            <div className="mt-2 text-xs text-gray-600">
              Remaining to supply:{" "}
              {Math.max(0, parseInt(item?.quantity || 0) - getTotalSupplied())}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {submittingForm ? (
              <div className="bg-blue-400 flex items-center gap-2 rounded-lg px-6 py-2 text-white">
                <RotatingLines strokeColor="white" height="16" width="16" />
                Submitting...
              </div>
            ) : (
              <button
                onClick={handleSupply}
                className="bg-blue-500 hover:bg-blue-600 rounded-lg px-6 py-2 text-white font-medium transition-colors flex items-center gap-2"
              >
                <CheckCircle size={16} />
                Supply Items
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SupplyItem;
